// Notificaciones locales (recordatorios programados en el dispositivo).
//
// No requieren servidor ni Firebase: la propia app las agenda con
// @capacitor/local-notifications. En web es todo no-op.
//
// La preferencia del recordatorio diario (activado + hora) vive en localStorage
// y es la fuente de verdad: la UI (NotificacionesSettings) la edita y el
// arranque (initLocalNotifications) la aplica.
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative, platform } from './platform';
import { planTips, planTurno, fechaConHora } from './notificacionesPlan';
import { planLanzamiento, enVentanaCampana } from './notificacionesLanzamiento';

// Dos canales a propósito: en Android el usuario silencia por canal, así que
// los tips (ruido opcional) van aparte de lo que cuelga de su turno (pre y post
// turno, ronda, pendiente con hora). Apagar los primeros no puede apagar el
// aviso de un fármaco de las 20:00.
const CHANNEL_ID = 'recordatorios';
const CHANNEL_TIPS_ID = 'tips';
const CHANNEL_NOVEDADES_ID = 'novedades-promociones';
const DAILY_REMINDER_ID = 1001;

const LS_ENABLED = 'mipae_notif_enabled';
const LS_HOUR = 'mipae_notif_hour';
const LS_MINUTE = 'mipae_notif_minute';

// Recordatorios de turno (pre-turno −1h y post-turno +30min), programados
// desde la rotación. IDs reservados 2000–2015.
const LS_TURNO_ENABLED = 'mipae_notif_turno';
const TURNO_BASE_ID = 2000;
const TURNO_IDS = 16;   // se cancela el rango completo, incluidos los de la versión vieja (7 días)

const DEFAULT_HOUR = 19;
const DEFAULT_MINUTE = 0;

// Lee la preferencia del recordatorio diario. Por defecto: activado a las 19:00.
export function getReminderPrefs() {
  const enabledRaw = localStorage.getItem(LS_ENABLED);
  return {
    enabled: enabledRaw === null ? true : enabledRaw === '1',
    hour: Number(localStorage.getItem(LS_HOUR) ?? DEFAULT_HOUR),
    minute: Number(localStorage.getItem(LS_MINUTE) ?? DEFAULT_MINUTE),
  };
}

// Pide permiso de notificaciones (Android 13+ lo exige en runtime).
export async function ensureNotificationPermission() {
  if (!isNative) return false;
  let perm = await LocalNotifications.checkPermissions();
  if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') {
    perm = await LocalNotifications.requestPermissions();
  }
  return perm.display === 'granted';
}

async function ensureChannel() {
  if (!isNative) return;
  try {
    await LocalNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'Recordatorios',
      description: 'Recordatorios de estudio y práctica clínica',
      importance: 4, // HIGH: aparece como heads-up
      visibility: 1,
    });
  } catch { /* createChannel sólo aplica en Android; ignorar en otros casos */ }
}

async function ensureChannelTips() {
  if (!isNative) return;
  try {
    await LocalNotifications.createChannel({
      id: CHANNEL_TIPS_ID,
      name: 'Tips clínicos',
      description: 'Un recordatorio clínico breve al día',
      importance: 3, // DEFAULT: llega a la bandeja, no interrumpe con heads-up
      visibility: 1,
    });
  } catch { /* createChannel sólo aplica en Android; ignorar en otros casos */ }
}

async function ensureChannelNovedades() {
  if (!isNative || platform !== 'android') return;
  await LocalNotifications.createChannel({
    id: CHANNEL_NOVEDADES_ID,
    name: 'Novedades y promociones',
    description: 'Funciones nuevas y pruebas gratuitas de MiPAE',
    importance: 1, // MIN: una promoción no es un aviso urgente del turno.
    visibility: 1,
  });
}

async function scheduleDaily(hour, minute) {
  await ensureChannel();
  await LocalNotifications.schedule({
    notifications: [{
      id: DAILY_REMINDER_ID,
      channelId: CHANNEL_ID,
      title: 'MiPAE',
      body: 'Tu reto de hoy está listo cuando quieras.',
      schedule: { on: { hour, minute }, allowWhileIdle: true },
    }],
  });
}

async function cancelDaily() {
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_REMINDER_ID }] });
  } catch { /* nada que cancelar */ }
}

// Guarda la preferencia y la aplica (agenda o cancela). Devuelve la preferencia
// efectiva. Si se pide activar pero el usuario niega el permiso, queda en false.
export async function setReminderPrefs({ enabled, hour = DEFAULT_HOUR, minute = DEFAULT_MINUTE }) {
  let effectiveEnabled = enabled;
  if (isNative) {
    if (enabled) {
      const granted = await ensureNotificationPermission();
      if (granted) await scheduleDaily(hour, minute);
      else effectiveEnabled = false;
    } else {
      await cancelDaily();
    }
  }
  localStorage.setItem(LS_ENABLED, effectiveEnabled ? '1' : '0');
  localStorage.setItem(LS_HOUR, String(hour));
  localStorage.setItem(LS_MINUTE, String(minute));
  return { enabled: effectiveEnabled, hour, minute };
}

// Recordatorio puntual (ej: retomar un PAE) en N horas.
export async function scheduleReminderIn({ id, title, body, hours = 24 }) {
  if (!isNative) return false;
  if (!(await ensureNotificationPermission())) return false;
  await ensureChannel();
  await LocalNotifications.schedule({
    notifications: [{
      id,
      channelId: CHANNEL_ID,
      title,
      body,
      schedule: { at: new Date(Date.now() + hours * 60 * 60 * 1000), allowWhileIdle: true },
    }],
  });
  return true;
}

// ── Recordatorio de ronda (M3) ────────────────────────────────────────────────
// Aviso único para la próxima ronda del turno (por defecto en 2 h). Se
// reprograma cada vez que se inicia o reinicia una ronda; nativo-only.
const RONDA_REMINDER_ID = 3001;

export async function scheduleRondaReminder({ minutos = 120 } = {}) {
  if (!isNative) return false;
  if (!(await ensureNotificationPermission())) return false;
  await ensureChannel();
  await LocalNotifications.schedule({
    notifications: [{
      id: RONDA_REMINDER_ID,
      channelId: CHANNEL_ID,
      title: 'Toca hacer la ronda',
      body: 'Recorre a tus pacientes y marca la ronda en MiPAE.',
      schedule: { at: new Date(Date.now() + minutos * 60 * 1000), allowWhileIdle: true },
    }],
  });
  return true;
}

export async function cancelRondaReminder() {
  if (!isNative) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: RONDA_REMINDER_ID }] });
  } catch { /* nada que cancelar */ }
}

// ── Recordatorio de pendiente con hora (fix #2 auditoría MiPAE en turno) ────────────
// Aviso puntual a la hora marcada de una tarea ("vanco 20:00", "HGT 17:00").
// Mismo patrón que scheduleRondaReminder: se agenda al crear la tarea y se
// cancela al marcarla hecha o borrarla; nativo-only. Las tareas usan
// `nuevoId()` (string) y LocalNotifications exige un id numérico, así que se
// hashea a un entero estable dentro de un rango propio (9 000 000+, lejos de
// los demás IDs reservados de este archivo).
const TAREA_REMINDER_BASE_ID = 9000000;
const TAREA_REMINDER_RANGO = 900000;

function hashTareaId(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % TAREA_REMINDER_RANGO;
}
function idTareaReminder(tareaId) {
  return TAREA_REMINDER_BASE_ID + hashTareaId(tareaId);
}

// Agenda el aviso de UNA tarea a su hora (hoy). Si la hora ya pasó, no tiene
// sentido agendarla (no se dispara "atrasada") — se omite en silencio.
export async function scheduleTareaReminder(tarea) {
  if (!isNative || !tarea?.id || !tarea?.hora) return false;
  const at = fechaConHora(new Date(), tarea.hora);
  if (at.getTime() <= Date.now() + 60 * 1000) return false;
  if (!(await ensureNotificationPermission())) return false;
  await ensureChannel();
  await LocalNotifications.schedule({
    notifications: [{
      id: idTareaReminder(tarea.id),
      channelId: CHANNEL_ID,
      title: 'Pendiente de tu turno',
      body: `${tarea.tipo}${tarea.detalle ? `: ${tarea.detalle}` : ''}, ${tarea.hora}`,
      schedule: { at, allowWhileIdle: true },
    }],
  });
  return true;
}

export async function cancelTareaReminder(tareaId) {
  if (!isNative || !tareaId) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: idTareaReminder(tareaId) }] });
  } catch { /* nada que cancelar */ }
}

// ── Recordatorios de turno ────────────────────────────────────────────────────

// Preferencia del recordatorio de turno (por dispositivo). Por defecto: activado.
export function getTurnoReminderPref() {
  const raw = localStorage.getItem(LS_TURNO_ENABLED);
  return raw === null ? true : raw === '1';
}

async function cancelTurnoReminders() {
  if (!isNative) return;
  const ids = [];
  for (let k = 0; k < TURNO_IDS; k++) ids.push({ id: TURNO_BASE_ID + k });
  try { await LocalNotifications.cancel({ notifications: ids }); } catch { /* nada que cancelar */ }
}

// Programa los recordatorios de los próximos turnos (pre −1h, post +30min).
// Reprograma desde cero cada vez (cancela los anteriores). Nativo-only.
// El QUÉ y el CUÁNDO los decide `planTurno` (puro y testeado); aquí sólo se
// entregan al sistema operativo.
export async function programarRecordatoriosTurno(rotacion) {
  if (!isNative) return false;
  await cancelTurnoReminders();
  if (!getTurnoReminderPref() || !rotacion?.patron) return false;
  if (!(await ensureNotificationPermission())) return false;
  await ensureChannel();

  const plan = planTurno({ ahora: new Date(), rotacion });
  const notifs = plan.map((n, k) => ({
    id: TURNO_BASE_ID + k, channelId: CHANNEL_ID,
    title: n.title, body: n.body,
    schedule: { at: n.at, allowWhileIdle: true },
  }));

  if (notifs.length) await LocalNotifications.schedule({ notifications: notifs });
  return notifs.length > 0;
}

// Guarda la preferencia del recordatorio de turno y la aplica.
export async function setTurnoReminderPref(enabled, rotacion) {
  let eff = enabled;
  if (isNative) {
    if (enabled) {
      const granted = await ensureNotificationPermission();
      if (granted) await programarRecordatoriosTurno(rotacion);
      else eff = false;
    } else {
      await cancelTurnoReminders();
    }
  }
  localStorage.setItem(LS_TURNO_ENABLED, eff ? '1' : '0');
  return eff;
}

// Llamado al arrancar la app en nativo: aplica la preferencia guardada.
export async function initLocalNotifications() {
  if (!isNative) return;
  const prefs = getReminderPrefs();
  if (!prefs.enabled) return; // respeta que el usuario lo haya apagado
  const granted = await ensureNotificationPermission();
  if (!granted) return;
  await scheduleDaily(prefs.hour, prefs.minute);
}

// ── Tips clínicos del día ("coach de bolsillo") ───────────────────────────────
// UN empujoncito clínico al día, a una hora que varía (~9–11) para que no se
// sienta robótico, y sólo dos días por delante. El contenido y los horarios
// los decide `lib/notificacionesPlan.js`, que además explica POR QUÉ el
// horizonte es corto (la avalancha del 2026-09-04: el sistema operativo puede
// posponer estas alarmas y entregarlas todas juntas, así que lo único que
// controlamos es cuántas puede llegar a acumular). Es opcional (toggle).
//
// Los IDs viven en 4000+ y NO en 3000+ como antes: el 3001 es el recordatorio
// de ronda, así que el rango viejo se pisaba con él (cancelar los tips
// cancelaba la ronda, y un tip podía sobrescribirla).
const LS_TIPS_ENABLED = 'mipae_notif_tips';
const TIPS_BASE_ID = 4000;
const TIPS_IDS = 8;              // holgura sobre el horizonte del plan; ids reservados 4000–4007

// TODO: eliminar 1-2 releases después del fix de la avalancha (2026-09-04).
// Limpieza one-shot del rango viejo de tips (3000–3039), que puede tener hasta
// 30 avisos agendados en los teléfonos que vienen de la versión anterior. Sin
// esto quedarían huérfanos para siempre: nadie los cancelaría nunca.
const TIPS_LEGACY_BASE_ID = 3000;
const TIPS_LEGACY_IDS = 40;

// Preferencia de los tips (por dispositivo). Por defecto: activado.
export function getTipsPref() {
  const raw = localStorage.getItem(LS_TIPS_ENABLED);
  return raw === null ? true : raw === '1';
}

async function cancelTips() {
  if (!isNative) return;
  const ids = [];
  for (let k = 0; k < TIPS_IDS; k++) ids.push({ id: TIPS_BASE_ID + k });
  for (let k = 0; k < TIPS_LEGACY_IDS; k++) {
    const id = TIPS_LEGACY_BASE_ID + k;
    if (id === RONDA_REMINDER_ID) continue;  // no barrer la ronda de un turno en curso
    ids.push({ id });
  }
  // Una baja fallida debe mostrarse; no afirmar que se retiraron los avisos.
  await LocalNotifications.cancel({ notifications: ids });
}

// Programa los tips de los próximos días. Reprograma desde cero cada vez.
// Nativo-only.
//
// Se serializa en una cola: la app llama a esto al arrancar y otra vez cuando
// carga la rotación, y dos corridas solapadas pueden dejar el cancel de una
// pisando el schedule de la otra (te quedas sin tips hasta la próxima apertura).
let colaTips = Promise.resolve();
export function programarTips() {
  const corrida = colaTips.then(programarTipsAhora, programarTipsAhora);
  // La cola guarda la versión ya "cazada": un fallo de una corrida no puede
  // dejar muertas a las siguientes ni quedar como rechazo sin manejar. El
  // llamador sí recibe el error, para que safeInit lo loguee.
  colaTips = corrida.catch(() => false);
  return corrida;
}

// La campaña tiene consentimiento propio, separado de los tips y de los
// recordatorios de turno. App entrega el perfil y el calendario reales.
let contextoLanzamiento = { profile: null, rotacion: null, overrides: [] };
export function fijarContextoLanzamiento({ profile = null, rotacion = null, overrides = [] } = {}) {
  contextoLanzamiento = { profile, rotacion, overrides: overrides || [] };
}

// La cuenta dueña del consentimiento. El perfil que carga App trae `user_id`
// (la columna de profiles), no `id`: leer solo `profile.id` daba undefined y en
// iPhone el interruptor de "Novedades y promociones" nunca se podía encender
// (revisión del 25-oct-2026). `id` queda como respaldo de los llamadores que
// ya lo entregaban así.
export function cuentaDe(profile) {
  return profile?.user_id || profile?.id || undefined;
}

// El consentimiento pertenece a esta cuenta en este dispositivo. En iPhone
// nunca se deduce del permiso de iOS ni del interruptor antiguo de tips.
const LS_PROMOCIONES = 'mipae_notif_promociones_v1:';
export function getPromocionesPref(userId = cuentaDe(contextoLanzamiento.profile)) {
  if (platform === 'ios' && !userId) return false;
  try {
    const raw = localStorage.getItem(LS_PROMOCIONES + (userId || 'dispositivo'));
    if (raw !== null) return JSON.parse(raw)?.aceptado === true;
    // Mientras el id no llegaba, Android guardó la elección bajo 'dispositivo':
    // se sigue respetando (sobre todo un "no") hasta que la persona la cambie.
    if (platform !== 'ios' && userId) {
      const previo = localStorage.getItem(LS_PROMOCIONES + 'dispositivo');
      if (previo !== null) return JSON.parse(previo)?.aceptado === true;
    }
    // Android conserva la preferencia previa; tiene su propio control desde aquí.
    return platform !== 'ios' && getTipsPref();
  } catch { return false; }
}

export async function setPromocionesPref(enabled, userId = cuentaDe(contextoLanzamiento.profile)) {
  if (platform === 'ios' && !userId) return false;
  let efectivo = enabled === true;
  if (efectivo && isNative) efectivo = await ensureNotificationPermission();
  // Si cambió la cuenta durante el permiso del sistema, no consentir por otra.
  if (userId !== cuentaDe(contextoLanzamiento.profile)) return false;
  localStorage.setItem(LS_PROMOCIONES + (userId || 'dispositivo'), JSON.stringify({
    aceptado: efectivo, fecha: new Date().toISOString(), texto: 'novedades-promociones-v1',
  }));
  // La baja se escribe ANTES de encolar: una programación en curso termina y
  // luego esta misma cola cancela sus avisos, sin tocar tareas ni rondas.
  await programarTips();
  return efectivo;
}

// Cuándo quedó programado el aviso "ya puedes probar" del 25. Si esa hora ya
// pasó, llegó: las próximas programaciones no lo repiten. Se marca recién
// cuando el sistema aceptó la programación (marcarPruebaProgramada).
const LS_PRUEBA_25 = 'mipae_notif_prueba25';
function pruebaYaAvisada(ahora) {
  try {
    const t = Date.parse(localStorage.getItem(LS_PRUEBA_25) || '');
    return Number.isFinite(t) && t <= +ahora;
  } catch {
    return false;
  }
}
export function marcarPruebaProgramada(plan = []) {
  const conPrueba = plan.find((n) => n.prueba);
  if (!conPrueba) return;
  try { localStorage.setItem(LS_PRUEBA_25, conPrueba.at.toISOString()); } catch { /* sin storage */ }
}

// Lo que se programa en el canal de tips: los avisos de la campaña y, fuera
// de sus días, el tip de siempre. Puro respecto del sistema operativo;
// exportado para las pruebas.
export function planCanalTips(ahora = new Date(), contexto = contextoLanzamiento) {
  const campana = getPromocionesPref(cuentaDe(contexto.profile)) ? planLanzamiento({
    ahora, profile: contexto.profile, rotacion: contexto.rotacion, overrides: contexto.overrides,
    pruebaAvisada: pruebaYaAvisada(ahora),
    // Conservamos los textos de herramientas en iPhone, además del consentimiento.
    sinPrueba: contexto.sinPrueba ?? platform === 'ios',
  }) : { activa: false, plan: [] };
  const tips = getTipsPref() ? planTips({ ahora }).filter((t) => !(campana.activa && enVentanaCampana(t.at))) : [];
  const promociones = campana.plan.map((n) => ({ ...n, promocional: true }));
  // El límite de dos incluye ambos tipos, también al terminar la campaña.
  return [...promociones, ...tips].sort((a, b) => a.at - b.at).slice(0, 2);
}

async function programarTipsAhora() {
  if (!isNative) return false;
  await cancelTips();
  if (!getTipsPref() && !getPromocionesPref()) return false;
  if (!(await ensureNotificationPermission())) return false;
  await ensureChannelTips();

  const plan = planCanalTips(new Date());
  if (plan.some((n) => n.promocional)) await ensureChannelNovedades();
  const notifs = plan.map((n, k) => ({
    id: TIPS_BASE_ID + k, channelId: n.promocional ? CHANNEL_NOVEDADES_ID : CHANNEL_TIPS_ID,
    title: n.title, body: n.body,
    schedule: { at: n.at, allowWhileIdle: true },
    ...(n.extra ? { extra: n.extra } : {}),
  }));

  if (notifs.length) {
    await LocalNotifications.schedule({ notifications: notifs });
    marcarPruebaProgramada(plan);
  }
  return notifs.length > 0;
}

// Guarda la preferencia de tips y la aplica.
export async function setTipsPref(enabled) {
  const eff = enabled && isNative ? await ensureNotificationPermission() : enabled;
  localStorage.setItem(LS_TIPS_ENABLED, eff ? '1' : '0');
  await programarTips();
  return eff;
}

// Notificación de prueba: dispara una en ~5 s para verificar permisos y entrega
// en el dispositivo, sin esperar a un horario real. ID propio (1099).
const PRUEBA_ID = 1099;
export async function dispararPrueba() {
  if (!isNative) return false;
  if (!(await ensureNotificationPermission())) return false;
  await ensureChannel();
  await LocalNotifications.schedule({
    notifications: [{
      id: PRUEBA_ID,
      channelId: CHANNEL_ID,
      title: 'MiPAE: prueba',
      body: 'Las notificaciones están activas. Así te avisaré de tus turnos.',
      schedule: { at: new Date(Date.now() + 5000), allowWhileIdle: true },
    }],
  });
  return true;
}

// Tocar un aviso local: le pasa su `extra` a App (la campaña lleva
// `destino: 'lo-nuevo'`). Devuelve la limpieza. En la web no hace nada.
export function escucharToqueNotificacion(alTocar) {
  if (!isNative) return () => {};
  let handle = null;
  let vivo = true;
  try {
    Promise.resolve(LocalNotifications.addListener('localNotificationActionPerformed', (evento) => {
      alTocar?.(evento?.notification?.extra || null);
    })).then((h) => { if (vivo) handle = h; else h?.remove?.(); }).catch(() => {});
  } catch { /* plugin ausente */ }
  return () => { vivo = false; handle?.remove?.(); };
}

